import detectVideoCodec, { needsTranscode } from './detectVideoCodec'

/**
 * Decide whether an uploaded video needs a server-side transcode to H.264.
 *
 * Background (#22523). Phase C1 (#21720) stopped forcing `.mov`/mp4/pdf uploads to Cloudinary when the
 * `s3MediaUpload` flag is on, on the reasoning that the new FileViewer no longer needs Cloudinary's
 * derivatives — true for PDFs (react-pdf) and for video *thumbnails* (native first-frame), but false
 * for video *decoding*, which nobody had listed as a Cloudinary responsibility. Cloudinary transcodes
 * on delivery, so an HEVC upload played; sent straight to S3 it plays audio over a black frame in
 * Chrome, Firefox and Edge, and its grid tile stays empty because that thumbnail needs a decode too.
 *
 * The fix is to transcode on S3 rather than to send the file back to Cloudinary. Routing it back would
 * have worked, but it keeps Cloudinary as a hard dependency for new uploads, which is the opposite of
 * what the ENG-883 epic exists to achieve.
 *
 * This runs in the **browser**, before the upload, where the `File` is already in hand — so reading the
 * codec costs nothing and needs no ranged HTTP request. Its answer decides whether the upload fires a
 * `videoTranscode` task at all, which is what keeps the ~84% of the library that is already H.264 from
 * paying for a MediaConvert job.
 *
 * Why this is not folded into `getRequiredTask`: that function receives only a MIME subtype, and the
 * codec is not derivable from a MIME type, a container, or a file extension. See `detectVideoCodec`.
 */

/**
 * MIME subtypes (`file.type.split('/')[1]`, as `getFileKey` computes `fileType`) whose containers are
 * ISOBMFF and can therefore be parsed for a codec.
 *
 * Deliberately narrow. `webm`, `mkv`, `avi` and `ogv` already upload straight to S3 whenever the S3
 * entitlement is on — the media forcing never covered them — so they are working today and are left
 * alone. Widening this list would start transcoding files nobody has reported a problem with.
 */
export const ISOBMFF_VIDEO_TYPES = ['mp4', 'm4v', 'quicktime']

export interface VideoTranscodeDecision {
  /** True when the upload should trigger a server-side transcode to H.264. */
  transcode: boolean
  /** The detected sample-entry fourcc, when one could be read. */
  codec?: string
  /** Why the decision came out this way. Logged, and asserted in tests. */
  reason: 'not-an-isobmff-video' | 'playable-codec' | 'unplayable-codec' | 'codec-undetectable' | 'detection-failed'
}

function isIsobmffVideo(file: { name?: string; type?: string }, fileType?: string): boolean {
  if (fileType && ISOBMFF_VIDEO_TYPES.includes(fileType.toLowerCase())) return true
  // `.mov` files sometimes arrive with an empty or odd MIME type, so check the name too. This mirrors
  // the `.mov` filename test the original media forcing used.
  if (file?.name?.toLowerCase().endsWith('.mov')) return true
  if (file?.name?.toLowerCase().endsWith('.m4v')) return true
  return file?.type === 'video/quicktime'
}

export default async function shouldTranscodeVideo({
  file,
  fileType
}: {
  file: Blob & { name?: string; type?: string }
  fileType?: string
}): Promise<VideoTranscodeDecision> {
  if (!file || !isIsobmffVideo(file, fileType)) {
    return { transcode: false, reason: 'not-an-isobmff-video' }
  }

  let codec: string | undefined
  try {
    codec = (await detectVideoCodec(file))?.codec
  } catch {
    // Detection must never break an upload. Ask for a transcode: the job re-probes server-side and
    // exits without a charge if the file turns out to be H.264 already.
    return { transcode: true, reason: 'detection-failed' }
  }

  if (!codec) {
    // Undetectable inside a container we expected to be able to parse — a truncated file, or a variant
    // this parser does not understand.
    //
    // This asks for a transcode, the opposite of `needsTranscode`'s own default, and the asymmetry is
    // intentional. `needsTranscode` guards a *paid* transcode from a bad codec string, so an unknown
    // costs nothing there. Here an unknown risks a silently black video for every non-Safari viewer,
    // and the server re-probes before it spends anything — so the cost of guessing wrong is one extra
    // free Probe call, while the cost of guessing wrong the other way is broken media.
    return { transcode: true, reason: 'codec-undetectable' }
  }

  return needsTranscode(codec)
    ? { transcode: true, codec, reason: 'unplayable-codec' }
    : { transcode: false, codec, reason: 'playable-codec' }
}
